/**
 * eBay-style Top Navigation
 *
 * Features:
 * - Top bar: Sign in, Daily Deals, Help, Sell | Ship to, Watchlist, My Boli, Notifications, Cart
 * - Main bar: Logo, Search with category dropdown, Search button
 * - Category bar: Category links with mega-menu on hover
 */

import { useEffect, useState } from 'react';
import { Search, ShoppingCart, Bell, Menu, ChevronDown, Heart, User, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCartStore } from '../lib/stores/cart-store';
import { useAuthStore } from '../lib/stores/auth-store';
import { useNotificationStore } from '../lib/stores/notification-store';
import { CategoryMegaMenu } from './CategoryMegaMenu';
import { NAV_CATEGORIES, CATEGORY_DATA } from '../lib/category-data';
import { cn } from '../lib/utils';

export function TopNav() {
  const navigate = useNavigate();
  const { itemCount, refreshCartCount, openDrawer } = useCartStore();
  const { isAuthenticated, user } = useAuthStore();
  const { unreadCount, refreshUnreadCount, initialize: initNotifications } = useNotificationStore();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  // Mega menu state
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Refresh cart count and notifications on mount and when auth changes
  useEffect(() => {
    if (isAuthenticated) {
      refreshCartCount();
      initNotifications();
    }
  }, [isAuthenticated, refreshCartCount, initNotifications]);

  // Poll for new notifications every 30 seconds when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      refreshUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshUnreadCount]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const params = new URLSearchParams();
      params.set('q', searchQuery.trim());
      if (selectedCategory !== 'all') {
        params.set('category', selectedCategory);
      }
      navigate(`/search?${params.toString()}`);
    }
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-white border-b border-gray-200">
      {/* Top Bar - Desktop Only */}
      <div className="hidden lg:block bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-9 text-xs">
            {/* Left Links */}
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <span className="text-gray-700">
                  Hi, <Link to="/profile" className="font-semibold text-primary hover:underline">{user?.firstName || 'User'}</Link>!
                </span>
              ) : (
                <span className="text-gray-700">
                  Hi! <Link to="/profile" className="text-primary hover:underline">Sign in</Link> or <Link to="/profile" className="text-primary hover:underline">register</Link>
                </span>
              )}
              <Link to="/deals" className="text-gray-700 hover:text-primary hover:underline">Daily Deals</Link>
              <Link to="/help" className="text-gray-700 hover:text-primary hover:underline">Help & Contact</Link>
            </div>

            {/* Right Links */}
            <div className="flex items-center gap-4">
              <Link to="/sell" className="text-gray-700 hover:text-primary hover:underline">Sell</Link>
              <Link to="/saved" className="text-gray-700 hover:text-primary hover:underline flex items-center gap-1">
                Watchlist <ChevronDown className="w-3 h-3" />
              </Link>
              <Link to="/profile" className="text-gray-700 hover:text-primary hover:underline flex items-center gap-1">
                My Boli <ChevronDown className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Bar */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 h-14 lg:h-16">
            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-gray-700 hover:text-primary"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <span className="text-2xl lg:text-3xl font-black tracking-tighter">
                <span className="text-[#E53238]">b</span>
                <span className="text-[#0064D2]">o</span>
                <span className="text-[#F5AF02]">l</span>
                <span className="text-[#86B817]">i</span>
              </span>
            </Link>

            {/* Shop by Category - Desktop */}
            <div className="hidden lg:block relative">
              <button
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-primary px-2 py-1"
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
              >
                Shop by<br />category <ChevronDown className="w-3 h-3" />
              </button>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex-1 flex">
              <div className="flex w-full max-w-3xl mx-auto">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 min-w-0 px-4 py-2.5 text-sm border-2 border-gray-300 border-r-0 rounded-l-full focus:outline-none focus:border-primary"
                  placeholder="Search for anything"
                />

                {/* Category Dropdown - Desktop */}
                <div className="hidden md:block relative">
                  <button
                    type="button"
                    onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                    className="flex items-center gap-2 h-full px-4 border-2 border-gray-300 border-l-0 border-r-0 bg-gray-50 text-sm text-gray-600 hover:bg-gray-100"
                  >
                    {selectedCategory === 'all' ? 'All Categories' : CATEGORY_DATA[selectedCategory]?.name || 'All Categories'}
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {isCategoryDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setIsCategoryDropdownOpen(false)} />
                      <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCategory('all');
                            setIsCategoryDropdownOpen(false);
                          }}
                          className={cn(
                            'w-full px-4 py-2 text-sm text-left hover:bg-gray-100',
                            selectedCategory === 'all' && 'bg-primary/10 text-primary font-medium'
                          )}
                        >
                          All Categories
                        </button>
                        {Object.values(CATEGORY_DATA).map((cat) => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => {
                              setSelectedCategory(cat.id);
                              setIsCategoryDropdownOpen(false);
                            }}
                            className={cn(
                              'w-full px-4 py-2 text-sm text-left hover:bg-gray-100',
                              selectedCategory === cat.id && 'bg-primary/10 text-primary font-medium'
                            )}
                          >
                            {cat.name}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <button
                  type="submit"
                  className="px-6 lg:px-8 bg-primary hover:bg-primary/90 text-white font-medium rounded-r-full transition-colors"
                >
                  <span className="hidden sm:inline">Search</span>
                  <Search className="w-5 h-5 sm:hidden" />
                </button>
              </div>
            </form>

            {/* Right Icons */}
            <div className="flex items-center gap-2 lg:gap-4">
              {/* Saved/Watchlist - Desktop */}
              <Link
                to="/saved"
                className="hidden lg:flex items-center gap-1 text-gray-600 hover:text-primary"
              >
                <Heart className="w-5 h-5" />
              </Link>

              {/* Notifications */}
              <Link
                to="/inbox"
                className="relative text-gray-600 hover:text-primary transition-colors"
              >
                <Bell className="w-6 h-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>

              {/* Cart */}
              <button
                onClick={openDrawer}
                className="relative text-gray-600 hover:text-primary transition-colors"
              >
                <ShoppingCart className="w-6 h-6" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-primary rounded-full">
                    {itemCount > 99 ? '99+' : itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Category Navigation Bar - Desktop */}
      <nav className="hidden lg:block bg-white border-t border-gray-100 relative">
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex items-center gap-6 h-10 text-sm">
            {NAV_CATEGORIES.map((cat) => (
              <li
                key={cat.id}
                className="relative h-full flex items-center"
                onMouseEnter={() => cat.hasMegaMenu && setActiveMegaMenu(cat.id)}
              >
                {cat.slug === 'sell' ? (
                  <Link
                    to="/seller/products/new"
                    className="text-gray-700 hover:text-primary transition-colors"
                  >
                    {cat.name}
                  </Link>
                ) : cat.slug === 'saved' ? (
                  <Link
                    to="/saved"
                    className="text-gray-700 hover:text-primary transition-colors"
                  >
                    {cat.name}
                  </Link>
                ) : cat.slug === 'deals' ? (
                  <Link
                    to="/?sort=deals"
                    className="text-gray-700 hover:text-primary transition-colors"
                  >
                    {cat.name}
                  </Link>
                ) : (
                  <Link
                    to={`/category/${cat.slug}`}
                    className={cn(
                      "text-gray-700 hover:text-primary transition-colors",
                      activeMegaMenu === cat.id && "text-primary"
                    )}
                  >
                    {cat.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Mega Menu - Rendered as overlay with proper hover zone */}
        {activeMegaMenu && CATEGORY_DATA[activeMegaMenu] && (
          <div
            className="absolute left-0 right-0 top-full"
            onMouseEnter={() => setActiveMegaMenu(activeMegaMenu)}
            onMouseLeave={() => setActiveMegaMenu(null)}
          >
            <CategoryMegaMenu
              categoryId={activeMegaMenu}
              isOpen={true}
              onClose={() => setActiveMegaMenu(null)}
            />
          </div>
        )}
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 max-w-full bg-white z-50 lg:hidden overflow-y-auto">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <span className="text-lg font-semibold">Menu</span>
              <button onClick={() => setIsMobileMenuOpen(false)}>
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            {/* User Section */}
            <div className="p-4 border-b border-gray-200">
              {isAuthenticated ? (
                <Link
                  to="/profile"
                  className="flex items-center gap-3"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{user?.firstName || 'User'}</p>
                    <p className="text-sm text-gray-500">View profile</p>
                  </div>
                </Link>
              ) : (
                <div className="space-y-2">
                  <Link
                    to="/profile"
                    className="block w-full py-2 px-4 bg-primary text-white text-center rounded-full font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/profile"
                    className="block w-full py-2 px-4 border border-primary text-primary text-center rounded-full font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>

            {/* Categories */}
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Shop by category
              </h3>
              <ul className="space-y-1">
                {NAV_CATEGORIES.filter(cat => cat.hasMegaMenu).map((cat) => (
                  <li key={cat.id}>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="flex items-center justify-between py-2 text-gray-700 hover:text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {cat.name}
                      <ChevronDown className="w-4 h-4 -rotate-90" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Links */}
            <div className="p-4 border-t border-gray-200">
              <ul className="space-y-1">
                <li>
                  <Link
                    to="/saved"
                    className="flex items-center gap-3 py-2 text-gray-700 hover:text-primary"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Heart className="w-5 h-5" />
                    Watchlist
                  </Link>
                </li>
                <li>
                  <Link
                    to="/seller/products/new"
                    className="flex items-center gap-3 py-2 text-gray-700 hover:text-primary"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Sell
                  </Link>
                </li>
                <li>
                  <Link
                    to="/help"
                    className="flex items-center gap-3 py-2 text-gray-700 hover:text-primary"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Help & Contact
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}
    </header>
  );
}
